import {getColorByPath, type ThemeColorPath, useFoundationTheme} from 'platform/foundation';

const GRID_COLOR: ThemeColorPath = 'palettes.neutral.30.100';
const AXIS_LABEL_COLOR: ThemeColorPath = 'text.secondary';
const CURSOR_COLOR: ThemeColorPath = 'palettes.neutral.20.100';
const LINE_CURSOR_COLOR: ThemeColorPath = 'palettes.neutral.40.100';
const REFERENCE_LINE_COLOR: ThemeColorPath = 'palettes.neutral.50.100';

// Recharts takes these values in pixels.
const AXIS_TICK_MARGIN = 8;
// Text box height of the 12 px axis labels, descenders included. The X axis band ends right
// below them, so the legend keeps its own spacing.
const AXIS_LABEL_HEIGHT = 12;
const BAR_CORNER_RADIUS = 4;
const LINE_WIDTH = 2;
const ACTIVE_DOT_RADIUS = 4;

// The card already pads the chart by 16 px on every side. The top keeps room for the upper half
// of the highest value label, which sits centred on the top grid line.
const CHART_MARGIN = {top: 5, right: 0, bottom: 0, left: 0};

// Only the data end is rounded, so a bar stays anchored to the baseline.
const BAR_RADIUS: [number, number, number, number] = [BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0, 0];

// A line chart puts its first and last point on the edges of the plot. Without this inset,
// Recharts hides the first month label and the SVG cuts the last dot in half.
const LINE_AXIS_PADDING = {left: 24, right: 24};

/**
 * Props that give the parts of a Recharts chart the shared look. Spread them onto the Recharts
 * components themselves. Recharts finds its parts by component type, so a wrapper component
 * around `CartesianGrid` or `XAxis` does not render. A value axis takes `useValueAxis`, which
 * builds on `yAxis`.
 */
export function useChartStyle() {
  const theme = useFoundationTheme();
  const axis = {
    axisLine: false,
    tickLine: false,
    // Recharts keeps room for tick lines even when they are hidden.
    tickSize: 0,
    tickMargin: AXIS_TICK_MARGIN,
    tick: {fill: getColorByPath(AXIS_LABEL_COLOR, theme), fontSize: theme.fontSizes.text.xSmall},
  };
  const xAxis = {...axis, height: AXIS_TICK_MARGIN + AXIS_LABEL_HEIGHT};

  return {
    margin: CHART_MARGIN,
    grid: {vertical: false, stroke: getColorByPath(GRID_COLOR, theme)},
    xAxis,
    lineXAxis: {...xAxis, padding: LINE_AXIS_PADDING},
    yAxis: axis,
    tooltipCursor: {fill: getColorByPath(CURSOR_COLOR, theme)},
    lineTooltipCursor: {stroke: getColorByPath(LINE_CURSOR_COLOR, theme)},
    referenceLine: {stroke: getColorByPath(REFERENCE_LINE_COLOR, theme)},
    // The dots stay: a point between two gaps has no line segment, so its dot is its only mark.
    line: {strokeWidth: LINE_WIDTH, activeDot: {r: ACTIVE_DOT_RADIUS}},
    barRadius: BAR_RADIUS,
    barCornerRadius: BAR_CORNER_RADIUS,
  };
}
